import React, { useState, useCallback, useEffect } from "react";
import * as Sentry from '@sentry/browser';
import { withAuth } from "../../hoc/Auth";
import {Column, Container, Wrapper} from "../../hoc/Layout";
import Api from "../../Api";
import { FormattedMessage } from "react-intl";
import TransactionAuditTimeline from "../../Components/TransactionAudit";
import classes from "./styles.module.css";

const SEARCH_TYPES = [
    { value: 'id', label: 'ID транзакции', placeholder: 'Например: 2399782', description: 'Внутренний числовой ID транзакции в системе' },
    { value: 'uuid', label: 'UUID транзакции', placeholder: 'Например: 45525d9b-5e5b-40c5-ad3d-1e7afbc6e0a5', description: 'UUID транзакции (поле uuid)' },
    { value: 'externalRef', label: 'External Reference', placeholder: 'Внешний идентификатор мерчанта', description: 'Идентификатор, переданный мерчантом при создании транзакции' },
    { value: 'hydraId', label: 'Processing Order ID', placeholder: 'Например: c68f72a6a73944ad882202cb7fe5ff10', description: 'ID заказа в процессинге (Hydra, FinStar и др.)' },
    { value: 'unlid', label: 'UNLID / Description', placeholder: 'Description транзакции', description: 'Поле description транзакции (UNLID или произвольное описание)' },
    { value: 'payoutRequestUuid', label: 'Payout Request UUID', placeholder: 'Например: 49c2c20b-db2e-4a57-86f4-b831f8cc996d', description: 'UUID заявки на выплату из CRM (PayoutRequest)' },
    { value: 'payload', label: 'Поиск по событиям', placeholder: 'Имя получателя, номер карты, любой текст...', description: 'Полнотекстовый поиск по payload и meta событий транзакции' },
];

const EVENT_TYPES = [
    { value: 'MERCHANT_REQUEST', label: 'Входящие запросы' },
    { value: 'MERCHANT_RESPONSE', label: 'Ответы мерчанту' },
    { value: 'PROVIDER_REQUEST', label: 'Запросы к провайдерам' },
    { value: 'PROVIDER_RESPONSE', label: 'Ответы от провайдеров' },
    { value: 'STATUS_CHANGE', label: 'Изменения статуса' },
    { value: 'MERCHANT_CALLBACK', label: 'Callbacks мерчанту' },
    { value: 'SYSTEM_NOTE', label: 'Системные заметки' },
];

const TransactionAudit = ({ token }) => {
    // Режимы: 'transaction' = поиск по транзакции, 'events' = поиск по событиям
    const [searchMode, setSearchMode] = useState('transaction');
    const [searchValue, setSearchValue] = useState("");
    const [searchType, setSearchType] = useState("id");

    // Для режима 'events'
    const [selectedEventTypes, setSelectedEventTypes] = useState([]);
    const [selectedMerchantId, setSelectedMerchantId] = useState(null);
    const [merchantSearch, setMerchantSearch] = useState("");
    const [merchantsList, setMerchantsList] = useState([]);
    const [, setLoadingMerchants] = useState(false);

    const [selectedProvider, setSelectedProvider] = useState("");
    const [providersList, setProvidersList] = useState([]);

    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [errorOnly, setErrorOnly] = useState(false);

    const [, setSearchId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [auditData, setAuditData] = useState(null);
    const [eventsList, setEventsList] = useState([]);
    const [recentSearches, setRecentSearches] = useState([]);
    const [expandedEventId, setExpandedEventId] = useState(null);

    const handleSearchTransaction = useCallback(async () => {
        const value = searchValue.trim();
        if (!value) {
            setError("Введите значение для поиска");
            return;
        }

        // Валидация для ID
        if (searchType === 'id') {
            const id = parseInt(value, 10);
            if (!id || isNaN(id)) {
                setError("ID должен быть числом");
                return;
            }
        }

        setLoading(true);
        setError(null);
        setEventsList([]);

        try {
            const data = await new Api({ token }).getTransactionAudit(value, { searchType });
            setAuditData(data);
            const trxId = data && data.transaction && data.transaction.id;
            setSearchId(trxId || value);

            // Сохраняем в историю поиска
            const newSearch = { type: searchType, value, id: trxId, time: new Date() };
            setRecentSearches(prev => [newSearch, ...prev.filter(s => s.value !== value).slice(0, 4)]);
        } catch (err) {
            Sentry.captureException(err);
            const errorMsg = (err.response && err.response.data && err.response.data.message)
                || err.message
                || "Не удалось загрузить данные";
            setError(errorMsg);
            setAuditData(null);
        } finally {
            setLoading(false);
        }
    }, [searchValue, searchType, token]);

    const handleSearchEvents = useCallback(async () => {
        if (selectedEventTypes.length === 0) {
            setError("Выберите тип события");
            return;
        }

        setLoading(true);
        setError(null);
        setAuditData(null);

        try {
            const filters = {
                eventTypes: selectedEventTypes,
            };

            if (selectedMerchantId) {
                filters.merchantId = selectedMerchantId;  // Отправляем ID (число)
            }
            if (selectedProvider) {
                filters.actors = [selectedProvider];  // Отправляем массив
            }
            if (dateFrom) {
                filters.dateRange = filters.dateRange || {};
                filters.dateRange.startDate = dateFrom;
            }
            if (dateTo) {
                filters.dateRange = filters.dateRange || {};
                filters.dateRange.endDate = dateTo;
            }
            if (errorOnly) {
                filters.hasError = true;
            }

            const data = await new Api({ token }).getTransactionEvents(filters);
            setEventsList(data.data || []);

            if ((data.data || []).length === 0) {
                setError("События не найдены");
            }
        } catch (err) {
            Sentry.captureException(err);
            const errorMsg = (err.response && err.response.data && err.response.data.message)
                || err.message
                || "Не удалось загрузить данные";
            setError(errorMsg);
            setEventsList([]);
        } finally {
            setLoading(false);
        }
    }, [selectedEventTypes, selectedMerchantId, selectedProvider, dateFrom, dateTo, errorOnly, token]);

    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            if (searchMode === 'transaction') {
                handleSearchTransaction();
            }
        }
    };

    // Загрузить провайдеров при монтировании
    useEffect(() => {
        const loadProviders = async () => {
            try {
                const data = await new Api({ token }).getTransactionEventActors();
                // Фильтруем только провайдеров (не merchant, system, admin)
                const providers = data.actors.filter(a =>
                    !['merchant', 'system', 'admin'].includes(a.code)
                );
                setProvidersList(providers.map(p => p.code));
            } catch (err) {
                Sentry.captureException(err);
                console.error('Failed to load providers:', err);
            }
        };
        loadProviders();
    }, [token]);

    // Функция для загрузки мерчантов при поиске
    const handleMerchantSearch = useCallback(async (query) => {
        setMerchantSearch(query);

        if (query.length < 2 && query.length > 0) {
            return; // Минимум 2 символа или пустая строка
        }

        setLoadingMerchants(true);
        try {
            const data = await new Api({ token }).getMerchants(query);
            setMerchantsList(data.merchants || []);
        } catch (err) {
            Sentry.captureException(err);
            console.error('Failed to load merchants:', err);
            setMerchantsList([]);
        } finally {
            setLoadingMerchants(false);
        }
    }, [token]);

    // Загрузить первые 100 мерчантов при монтировании
    useEffect(() => {
        handleMerchantSearch('');
    }, [handleMerchantSearch]);

    const handleRecentClick = (search) => {
        setSearchValue(search.value);
        setSearchType(search.type);
        setSearchMode('transaction');
    };

    const clearFilters = () => {
        setSelectedEventTypes([]);
        setSelectedMerchantId(null);
        setMerchantSearch("");
        setSelectedProvider("");
        setDateFrom("");
        setDateTo("");
        setErrorOnly(false);
        setEventsList([]);
        handleMerchantSearch(''); // Перезагрузить список
    };

    const currentSearchType = SEARCH_TYPES.find(t => t.value === searchType);

    return (
        <Wrapper>
            <Container style={{justifyContent: 'center'}}>
                <Column width={1180}>
                    <h1 className={classes.Title}>
                        <FormattedMessage id="TransactionAudit.page.title" defaultMessage="Transaction Audit" />
                    </h1>

                    {/* Выбор режима поиска */}
                    <div className={classes.ModeSelector}>
                        <button
                            className={`${classes.ModeBtn} ${searchMode === 'transaction' ? classes.ModeBtnActive : ''}`}
                            onClick={() => setSearchMode('transaction')}
                        >
                            По транзакции
                        </button>
                        <button
                            className={`${classes.ModeBtn} ${searchMode === 'events' ? classes.ModeBtnActive : ''}`}
                            onClick={() => setSearchMode('events')}
                        >
                            По событиям
                        </button>
                    </div>

                    <div className={classes.SearchSection}>
                        {searchMode === 'transaction' ? (
                            <div>
                                <div className={classes.SearchTypeSelector}>
                                    {SEARCH_TYPES.map(type => (
                                        <button
                                            key={type.value}
                                            className={`${classes.SearchTypeBtn} ${searchType === type.value ? classes.SearchTypeBtnActive : ''}`}
                                            onClick={() => setSearchType(type.value)}
                                            title={type.description}
                                        >
                                            {type.label}
                                        </button>
                                    ))}
                                </div>
                                {currentSearchType && currentSearchType.description && (
                                    <div className={classes.SearchTypeHint}>
                                        {currentSearchType.description}
                                    </div>
                                )}

                                <div className={classes.SearchBox}>
                                    <input
                                        type="text"
                                        className={classes.SearchInput}
                                        placeholder={(currentSearchType && currentSearchType.placeholder) || 'Введите значение'}
                                        value={searchValue}
                                        onChange={(e) => setSearchValue(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                    />
                                    <button
                                        className={classes.SearchButton}
                                        onClick={handleSearchTransaction}
                                        disabled={loading}
                                    >
                                        {loading ? "Поиск..." : "Найти"}
                                    </button>
                                </div>

                                {recentSearches.length > 0 && (
                                    <div className={classes.RecentSearches}>
                                        <span className={classes.RecentLabel}>Недавние:</span>
                                        {recentSearches.map((search, idx) => (
                                            <button
                                                key={idx}
                                                className={classes.RecentItem}
                                                onClick={() => handleRecentClick(search)}
                                                title={(() => { const found = SEARCH_TYPES.find(t => t.value === search.type); return ((found && found.label) || search.type) + ': ' + search.value; })()}
                                            >
                                                #{search.id || search.value.substring(0, 8)}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div>
                                <div className={classes.EventsFilters}>
                                    <div className={classes.FilterGroup}>
                                        <label className={classes.FilterLabel}>Тип события:</label>
                                        <div className={classes.FilterChips}>
                                            {EVENT_TYPES.map(type => (
                                                <button
                                                    key={type.value}
                                                    className={`${classes.FilterChip} ${selectedEventTypes.includes(type.value) ? classes.FilterChipActive : ''}`}
                                                    onClick={() => setSelectedEventTypes(prev =>
                                                        prev.includes(type.value)
                                                            ? prev.filter(t => t !== type.value)
                                                            : [...prev, type.value]
                                                    )}
                                                >
                                                    {type.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className={classes.FilterRow}>
                                        <div className={classes.FilterItem}>
                                            <label>Мерчант:</label>
                                            <div className={classes.ComboBox}>
                                                <input
                                                    type="text"
                                                    placeholder="Начните печатать название..."
                                                    value={merchantSearch}
                                                    onChange={(e) => handleMerchantSearch(e.target.value)}
                                                    className={classes.ComboBoxInput}
                                                />
                                                {merchantsList.length > 0 && (
                                                    <div className={classes.ComboBoxDropdown}>
                                                        {merchantsList.map(merchant => (
                                                            <div
                                                                key={merchant.id}
                                                                className={`${classes.ComboBoxOption} ${selectedMerchantId === merchant.id ? classes.ComboBoxOptionActive : ''}`}
                                                                onClick={() => {
                                                                    setSelectedMerchantId(merchant.id);
                                                                    setMerchantSearch(merchant.name);
                                                                    setMerchantsList([]);
                                                                }}
                                                            >
                                                                {merchant.name} <span className={classes.MerchantId}>#{merchant.id}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {selectedMerchantId && (
                                                    <button
                                                        className={classes.ClearButton}
                                                        onClick={() => {
                                                            setSelectedMerchantId(null);
                                                            setMerchantSearch('');
                                                            handleMerchantSearch('');
                                                        }}
                                                        title="Очистить"
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className={classes.FilterItem}>
                                            <label>Провайдер:</label>
                                            <select
                                                value={selectedProvider}
                                                onChange={(e) => setSelectedProvider(e.target.value)}
                                                className={classes.SelectInput}
                                            >
                                                <option value="">-- Все провайдеры --</option>
                                                {providersList.map(provider => (
                                                    <option key={provider} value={provider}>
                                                        {provider}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className={classes.FilterItem}>
                                            <label>От (дата/время):</label>
                                            <input
                                                type="datetime-local"
                                                value={dateFrom}
                                                onChange={(e) => setDateFrom(e.target.value)}
                                            />
                                        </div>

                                        <div className={classes.FilterItem}>
                                            <label>До (дата/время):</label>
                                            <input
                                                type="datetime-local"
                                                value={dateTo}
                                                onChange={(e) => setDateTo(e.target.value)}
                                            />
                                        </div>

                                        <div className={classes.FilterItem}>
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={errorOnly}
                                                    onChange={(e) => setErrorOnly(e.target.checked)}
                                                />
                                                Только ошибки
                                            </label>
                                        </div>
                                    </div>

                                    <div className={classes.FilterActions}>
                                        <button
                                            className={classes.SearchButton}
                                            onClick={handleSearchEvents}
                                            disabled={loading || selectedEventTypes.length === 0}
                                        >
                                            {loading ? "Поиск..." : "Поиск событий"}
                                        </button>
                                        <button
                                            className={classes.ClearButton}
                                            onClick={clearFilters}
                                        >
                                            Сброс
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {error && (
                        <div className={classes.ErrorBox}>
                            {error}
                        </div>
                    )}

                    {/* Результаты поиска по событиям */}
                    {searchMode === 'events' && eventsList.length > 0 && (
                        <div className={classes.ResultSection}>
                            <h2 className={classes.SectionTitle}>
                                События ({eventsList.length})
                            </h2>
                            <div className={classes.EventsList}>
                                {eventsList.map(event => (
                                    <div key={event.id} className={classes.EventItem}>
                                        <div
                                            className={classes.EventItemHeader}
                                            onClick={() => setExpandedEventId(expandedEventId === event.id ? null : event.id)}
                                        >
                                            <div className={classes.EventItemTime}>
                                                {new Date(event.createdAt).toLocaleString()}
                                            </div>
                                            <div className={classes.EventItemType}>
                                                {event.eventType}
                                            </div>
                                            {event.actor && (
                                                <div className={classes.EventItemActor}>
                                                    {event.actor}
                                                </div>
                                            )}
                                            {event.direction && (
                                                <div className={classes.EventItemDirection}>
                                                    {event.direction}
                                                </div>
                                            )}
                                            {event.httpCode && (
                                                <div className={`${classes.EventItemHttpCode} ${event.httpCode >= 200 && event.httpCode < 300 ? classes.HttpOk : classes.HttpError}`}>
                                                    HTTP {event.httpCode}
                                                </div>
                                            )}
                                            {event.errorCode && (
                                                <div className={classes.EventItemError}>
                                                    ❌ {event.errorCode}
                                                </div>
                                            )}
                                            <div className={classes.EventItemTransactionId}>
                                                Трансакция: #{event.transactionId}
                                            </div>
                                            <div className={classes.ExpandIcon}>
                                                {expandedEventId === event.id ? '▼' : '▶'}
                                            </div>
                                        </div>

                                        {expandedEventId === event.id && (
                                            <div className={classes.EventItemDetails}>
                                                {event.errorMessage && (
                                                    <div className={classes.ErrorDetails}>
                                                        <strong>Ошибка:</strong> {event.errorMessage}
                                                    </div>
                                                )}
                                                {event.payload && (
                                                    <div className={classes.PayloadDetails}>
                                                        <strong>Payload:</strong>
                                                        <pre>{JSON.stringify(event.payload, null, 2)}</pre>
                                                    </div>
                                                )}
                                                {event.meta && (
                                                    <div className={classes.MetaDetails}>
                                                        <strong>Meta:</strong>
                                                        <pre>{JSON.stringify(event.meta, null, 2)}</pre>
                                                    </div>
                                                )}
                                                {event.statusFrom && event.statusTo && (
                                                    <div className={classes.StatusChange}>
                                                        <strong>Статус:</strong> {event.statusFrom} → {event.statusTo}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Результаты поиска по транзакции */}
                    {searchMode === 'transaction' && auditData && auditData.transaction && (
                        <div className={classes.ResultSection}>
                            <div className={classes.TransactionInfo}>
                                <h2 className={classes.SectionTitle}>
                                    <FormattedMessage id="TransactionAudit.transaction" defaultMessage="Transaction" /> #{auditData.transaction.id}
                                </h2>
                                <div className={classes.InfoGrid}>
                                    <div className={classes.InfoItem}>
                                        <span className={classes.InfoLabel}>
                                            <FormattedMessage id="TransactionAudit.status" defaultMessage="Status" />
                                        </span>
                                        <span className={classes.InfoValue}>{auditData.transaction.status || "—"}</span>
                                    </div>
                                    <div className={classes.InfoItem}>
                                        <span className={classes.InfoLabel}>
                                            <FormattedMessage id="TransactionAudit.amount" defaultMessage="Amount" />
                                        </span>
                                        <span className={classes.InfoValue}>
                                            {auditData.transaction.amount} {auditData.transaction.currency}
                                        </span>
                                    </div>
                                    <div className={classes.InfoItem}>
                                        <span className={classes.InfoLabel}>
                                            <FormattedMessage id="TransactionAudit.fee" defaultMessage="Fee" />
                                        </span>
                                        <span className={classes.InfoValue}>
                                            {auditData.transaction.fee != null ? auditData.transaction.fee : "—"}
                                        </span>
                                    </div>
                                    <div className={classes.InfoItem}>
                                        <span className={classes.InfoLabel}>
                                            <FormattedMessage id="TransactionAudit.merchant" defaultMessage="Merchant" />
                                        </span>
                                        <span className={classes.InfoValue}>{auditData.transaction.merchantName || "—"}</span>
                                    </div>
                                    <div className={classes.InfoItem}>
                                        <span className={classes.InfoLabel}>
                                            <FormattedMessage id="TransactionAudit.terminalId" defaultMessage="Terminal ID" />
                                        </span>
                                        <span className={classes.InfoValue}>{auditData.transaction.terminalId || "—"}</span>
                                    </div>
                                    <div className={classes.InfoItem}>
                                        <span className={classes.InfoLabel}>
                                            <FormattedMessage id="TransactionAudit.cardMask" defaultMessage="Card" />
                                        </span>
                                        <span className={classes.InfoValue}>{auditData.transaction.cardMask || "—"}</span>
                                    </div>
                                    <div className={classes.InfoItem}>
                                        <span className={classes.InfoLabel}>
                                            <FormattedMessage id="TransactionAudit.cardHolder" defaultMessage="Card Holder" />
                                        </span>
                                        <span className={classes.InfoValue}>{auditData.transaction.cardHolder || "—"}</span>
                                    </div>
                                    <div className={classes.InfoItem}>
                                        <span className={classes.InfoLabel}>
                                            <FormattedMessage id="TransactionAudit.rrn" defaultMessage="RRN" />
                                        </span>
                                        <span className={classes.InfoValue}>{auditData.transaction.rrn || "—"}</span>
                                    </div>
                                    <div className={classes.InfoItem}>
                                        <span className={classes.InfoLabel}>
                                            <FormattedMessage id="TransactionAudit.authcode" defaultMessage="Auth Code" />
                                        </span>
                                        <span className={classes.InfoValue}>{auditData.transaction.authcode || "—"}</span>
                                    </div>
                                    <div className={classes.InfoItem}>
                                        <span className={classes.InfoLabel}>
                                            <FormattedMessage id="TransactionAudit.processingId" defaultMessage="Processing ID" />
                                        </span>
                                        <span className={classes.InfoValue}>{auditData.transaction.processingOrderId || "—"}</span>
                                    </div>
                                    <div className={classes.InfoItem}>
                                        <span className={classes.InfoLabel}>
                                            <FormattedMessage id="TransactionAudit.externalRef" defaultMessage="External Ref" />
                                        </span>
                                        <span className={classes.InfoValue}>{auditData.transaction.externalReference || "—"}</span>
                                    </div>
                                    <div className={classes.InfoItem}>
                                        <span className={classes.InfoLabel}>
                                            <FormattedMessage id="TransactionAudit.clientIp" defaultMessage="Client IP" />
                                        </span>
                                        <span className={classes.InfoValue}>{auditData.transaction.clientIp || "—"}</span>
                                    </div>
                                    <div className={classes.InfoItem}>
                                        <span className={classes.InfoLabel}>
                                            <FormattedMessage id="TransactionAudit.createdAt" defaultMessage="Created" />
                                        </span>
                                        <span className={classes.InfoValue}>
                                            {auditData.transaction.createdAt ? new Date(auditData.transaction.createdAt).toLocaleString() : "—"}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className={classes.TimelineSection}>
                                <h2 className={classes.SectionTitle}>
                                    <FormattedMessage id="TransactionAudit.timeline" defaultMessage="Event Timeline" />
                                </h2>
                                <TransactionAuditTimeline key={auditData.transaction.id} transactionId={auditData.transaction.id} token={token} initialData={auditData} />
                            </div>
                        </div>
                    )}

                    {!auditData && eventsList.length === 0 && !loading && !error && (
                        <div className={classes.EmptyState}>
                            {searchMode === 'transaction' ? (
                                <FormattedMessage
                                    id="TransactionAudit.hint"
                                    defaultMessage="Enter a transaction ID to view its complete audit history"
                                />
                            ) : (
                                "Выберите тип события и нажмите 'Поиск событий' чтобы найти события"
                            )}
                        </div>
                    )}
                </Column>
            </Container>
        </Wrapper>
    );
};

export default withAuth(TransactionAudit);



// WEBPACK FOOTER //
// ./src/PagesOfAdministrator/TransactionAudit/index.js